import React from 'react';
import { Check, Zap, Sparkles, Building2, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';
import { formatPrice } from '../../data/constants';
import { LOCALES } from '../../lib/i18n';

const ICONS = { free: Sparkles, pro: Zap, business: Building2 };

const CTA_CLASSES = {
  popular: 'bg-[#F2B441] hover:bg-[#E8A82E] text-[#1A1A1A]',
  free: 'bg-[#FAF7F2] hover:bg-[#EFEAE0] text-[#1A1A1A]',
  default: 'bg-[#0F8B5C] hover:bg-[#0A6E47] text-white',
};

const getCtaClass = (plan) => {
  if (plan.popular) return CTA_CLASSES.popular;
  if (plan.id === 'free') return CTA_CLASSES.free;
  return CTA_CLASSES.default;
};

const PriceLabel = ({ plan, popular }) => {
  const { t, currency, lang } = useApp();
  const textCls = popular ? 'text-white' : 'text-[#1A1A1A]';
  const subCls = popular ? 'text-white/60' : 'text-[#7A7468]';
  if (plan.price === 0) {
    return <div className="mt-4 flex items-baseline gap-1"><span className={`text-5xl font-black tracking-tight ${textCls}`}>0</span></div>;
  }
  const amount = formatPrice(plan.price, 'XOF', currency, LOCALES[lang]).split(' ')[0];
  const unit = currency === 'XOF' ? 'CFA' : currency;
  return (
    <div className="mt-4 flex items-baseline gap-1">
      <span className={`text-5xl font-black tracking-tight ${textCls}`}>{amount}</span>
      <span className={`text-sm ${subCls}`}>{unit}{t('pricing.month')}</span>
    </div>
  );
};

export const PlanCard = ({ plan, loading, onSelect }) => {
  const { t, lang } = useApp();
  const Icon = ICONS[plan.id];
  const popular = Boolean(plan.popular);
  const features = plan.features[lang] || plan.features.en;
  const ctaLabel = loading ? t('pricing.redirecting') : plan.cta[lang] || plan.cta.en;

  return (
    <div data-testid={`plan-card-${plan.id}`} className={`relative rounded-3xl p-8 border transition-all hover:-translate-y-1 ${popular ? 'bg-[#1A1A1A] border-[#1A1A1A] text-white shadow-2xl scale-105' : 'bg-white border-[#EFEAE0]'}`}>
      {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#F2B441] text-[#1A1A1A] text-xs font-bold uppercase tracking-wider">{t('pricing.popular')}</span>}
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${popular ? 'bg-[#F2B441]/20' : 'bg-[#0F8B5C]/10'}`}>
        <Icon className={`w-6 h-6 ${popular ? 'text-[#F2B441]' : 'text-[#0F8B5C]'}`} />
      </div>
      <h3 className={`text-2xl font-black tracking-tight ${popular ? 'text-white' : 'text-[#1A1A1A]'}`}>{plan.name[lang] || plan.name.en}</h3>
      <PriceLabel plan={plan} popular={popular} />
      <ul className={`mt-6 space-y-3 ${popular ? 'text-white/90' : 'text-[#3F3A30]'}`}>
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${popular ? 'bg-[#F2B441]' : 'bg-[#0F8B5C]/10'}`}>
              <Check className={`w-3 h-3 ${popular ? 'text-[#1A1A1A]' : 'text-[#0F8B5C]'}`} strokeWidth={3} />
            </div>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button onClick={() => onSelect(plan.id)} disabled={loading} data-testid={`plan-cta-${plan.id}`} className={`w-full mt-8 h-11 font-semibold ${getCtaClass(plan)}`}>
        {ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};
